import {
  isClientInteractionEnabled,
  observeClientInteraction,
  reportClientInteractionError,
} from '@github-ui/ahp-relay/relay-command-observation'
import {
  canPrepareCommandPayload,
  matchesCommandPayload as matchesPrompt,
  snapshotCommandPayload as snapshotPrompt,
} from '@github-ui/ahp-session-client/client-command-payload'
import {
  bindClientCommand,
  type ClientCommandObserver,
  clientCommandObserver,
  clientCommandPreparationIntegrity,
  recordClientCommandPreparation,
} from '@github-ui/ahp-session-client/client-interaction-observer'
import {
  createEstablishedAhpPendingMessage,
  type EstablishedAhpSessionIdentity,
  type PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import {browserObservationTime, nextBrowserObservationSequence} from './browser-observation-clock'
import {createClientInteractionMetricObserver} from './browser-outcome-metrics'
import {createBrowserClientInteractionTracker, MAX_PENDING_CLIENT_INTERACTIONS} from './client-interaction'
import {
  CLIENT_INTERACTION_EVENT,
  type ClientInteraction,
  type ClientInteractionBinding,
  type ClientInteractionFact,
  type ClientInteractionKind,
  type ClientInteractionStart,
  type ClientInteractionUnknown,
} from './client-interaction-contract'
import {sendEvent} from '@github-ui/hydro-analytics'

export type ClientInteractionEvent = Event | {readonly nativeEvent: Event}
export interface CopilotClientInteractionContext extends ClientInteractionBinding {
  readonly surface?: ClientInteractionFact['surface']
  readonly role: ClientInteractionFact['role']
  readonly client_view_id?: string
}

interface CommandState {
  readonly context: CopilotClientInteractionContext
  inputValue: string
  accepted: boolean
  pending: boolean
  transportSent: boolean
  handoffRequired: boolean
  expectedPendingMessageId?: string
  requestFeedback(): void
  discardFeedback(): void
  release(): void
}

const events = new WeakMap<Event, ClientInteraction>()
const commands = new WeakMap<ClientInteraction, CommandState>()
const observerInteractions = new WeakMap<ClientCommandObserver, ClientInteraction>()
const interactionMetrics = createClientInteractionMetricObserver(undefined, () =>
  reportClientInteractionError('emission'),
)

export function clientInteractionForCommand(prompt: PreparedPrompt): ClientInteraction | undefined {
  const observer = clientCommandObserver(prompt)
  return observer ? observerInteractions.get(observer) : undefined
}
const queuedInteractions = new WeakMap<Parameters<typeof createEstablishedAhpPendingMessage>[0], ClientInteraction>()
const queuedRevisions = new Map<string, {id: string; revision: number}>()

function pendingKey(sessionId: string | undefined, chatId: string | undefined, id: string): string {
  return JSON.stringify([sessionId, chatId, id])
}

function retainRevision(key: string, id: string, revision: number) {
  queuedRevisions.delete(key)
  queuedRevisions.set(key, {id, revision})
  if (queuedRevisions.size > MAX_PENDING_CLIENT_INTERACTIONS) {
    const oldest = queuedRevisions.keys().next().value
    if (oldest !== undefined) queuedRevisions.delete(oldest)
    reportClientInteractionError('observation_capacity')
  }
}

export function clientInteractionForQueuedMessage(message: Parameters<typeof createEstablishedAhpPendingMessage>[0]) {
  return queuedInteractions.get(message)
}

export function clientInteractionForEvent(event?: ClientInteractionEvent): ClientInteraction | undefined {
  return event ? events.get('nativeEvent' in event ? event.nativeEvent : event) : undefined
}

export function clientInteractionNotStarted(interaction: ClientInteraction | undefined): void {
  if (!interaction) return
  observeClientInteraction(() => {
    interaction.unresolved('not_started')
    const state = commands.get(interaction)
    state?.discardFeedback()
    state?.release()
  })
}

export function clientInteractionPending(interaction: ClientInteraction | undefined): void {
  if (!interaction) return
  observeClientInteraction(() => {
    const state = commands.get(interaction)
    if (state) {
      state.pending = true
      state.requestFeedback()
    }
  })
}

export function prepareLegacyClientInteraction(interaction: ClientInteraction | undefined, inputValue: string): void {
  if (!interaction) return
  observeClientInteraction(() => {
    const state = commands.get(interaction)
    if (!state) return
    interaction.preparationStarted()
    state.inputValue = inputValue
    interaction.preparationCompleted()
  })
}

export function clientInteractionAccepted(interaction: ClientInteraction | undefined): void {
  if (!interaction) return
  observeClientInteraction(() => {
    const state = commands.get(interaction)
    if (state) {
      state.accepted = true
      if (state.handoffRequired && !state.transportSent) interaction.unresolved('handoff_unobserved')
      state.requestFeedback()
    }
  })
}

export function clientInteractionFailed(interaction: ClientInteraction | undefined): void {
  if (!interaction) return
  const state = commands.get(interaction)
  if (!state?.transportSent) interaction.failed('preparation_failed')
  state?.release()
}

function sameSession(expected: ClientInteractionBinding, identity: EstablishedAhpSessionIdentity, channel: string) {
  return (
    (!expected.session_id || expected.session_id === identity.sessionId) &&
    (!expected.environment_id || expected.environment_id === identity.environmentId) &&
    (!expected.task_id || expected.task_id === identity.taskId) &&
    (!expected.chat_id || expected.chat_id === channel) &&
    (!identity.channel || !identity.channel.startsWith('ahp-chat:') || identity.channel === channel)
  )
}

export function prepareClientInteraction(interaction: ClientInteraction | undefined, prompt: PreparedPrompt): void {
  if (!interaction) return
  observeClientInteraction(() => {
    const state = commands.get(interaction)
    if (!state) return
    let expected = snapshotPrompt(prompt)
    let expectedBinding: ClientInteractionBinding = state.context
    let sessionVerified = Boolean(state.context.session_id)
    const preparationIntegrity = clientCommandPreparationIntegrity(prompt)
    const intentVerified =
      preparationIntegrity ??
      (prompt.text === state.inputValue &&
        !prompt.attachments?.length &&
        prompt.model === undefined &&
        prompt.agent === undefined &&
        prompt.contributedInstructions === undefined)
    if (preparationIntegrity === false) interaction.failed('attachment_loss')
    const observer: ClientCommandObserver = {
      bind(identity) {
        if (!identity.sessionId || !identity.environmentId) {
          interaction.unresolved('session_unverified')
          return
        }
        const chat = identity.channel?.startsWith('ahp-chat:') ? identity.channel : (expectedBinding.chat_id ?? '')
        if (!sameSession(expectedBinding, identity, chat)) {
          interaction.failed('session_mismatch')
          return
        }
        expectedBinding = {
          ...expectedBinding,
          session_id: identity.sessionId,
          environment_id: identity.environmentId,
          ...(identity.taskId ? {task_id: identity.taskId} : {}),
          ...(identity.channel?.startsWith('ahp-chat:') ? {chat_id: identity.channel} : {}),
        }
        sessionVerified = true
        interaction.dependencyChanged('session', false)
      },
      prepared(source, target, transform) {
        if (!canPrepareCommandPayload(expected, source, target, transform)) {
          interaction.failed('payload_mismatch')
        } else {
          expected = snapshotPrompt(target)
        }
      },
      sent(identity, channel, action, requestId) {
        state.transportSent = true
        const payloadMatches =
          matchesPrompt(expected, action) &&
          (interaction.kind !== 'queue' || (action.type === 'chat/pendingMessageSet' && action.kind === 'queued')) &&
          (state.expectedPendingMessageId === undefined ||
            action.type === 'chat/turnStarted' ||
            (action.type === 'chat/pendingMessageSet' &&
              action.id === state.expectedPendingMessageId &&
              action.kind === 'steering'))
        const sessionMatches = sessionVerified ? sameSession(expectedBinding, identity, channel) : undefined
        interaction.handoff(
          {
            session_id: identity.sessionId,
            environment_id: identity.environmentId,
            ...(identity.taskId ? {task_id: identity.taskId} : {}),
            chat_id: channel,
            ...(requestId ? {submission_request_id: requestId} : {}),
            ...(action.type === 'chat/turnStarted' ? {turn_id: action.turnId} : {}),
            ...(action.type === 'chat/pendingMessageSet' && state.expectedPendingMessageId === undefined
              ? {pending_message_id: action.id}
              : {}),
          },
          intentVerified ? payloadMatches : undefined,
          sessionMatches,
        )
        state.release()
        if (
          payloadMatches &&
          intentVerified &&
          sessionMatches &&
          action.type === 'chat/pendingMessageSet' &&
          interaction.commandId &&
          interaction.commandRevision
        ) {
          retainRevision(
            pendingKey(identity.sessionId, channel, action.id),
            interaction.commandId,
            interaction.commandRevision,
          )
        }
      },
      failed: reason => interaction.failed(reason ?? 'transport_failed'),
      unavailable: () => interaction.unresolved('handoff_unobserved'),
      waiting: (waiting, dependency = 'mutation_lane') => interaction.dependencyChanged(dependency, waiting),
      disposition: reason => interaction.unresolved(reason),
    }
    bindClientCommand(prompt, observer)
    observerInteractions.set(observer, interaction)
    interaction.preparationCompleted()
  })
}

export function createStopInteractionObserver(
  interaction: ClientInteraction | undefined,
  turnId: string | undefined,
): ClientCommandObserver | undefined {
  if (!interaction) return
  return observeClientInteraction(() => {
    const state = commands.get(interaction)
    if (!state) return undefined
    let expectedTurn = turnId
    let expectedBinding = state.context
    let sessionVerified = Boolean(expectedBinding.session_id)
    let sentObserved = false
    return {
      bindTarget(identity, target) {
        if (sentObserved) return
        if (!target || !identity.sessionId || !identity.environmentId) {
          interaction.unresolved('stop_target_unverified')
          return
        }
        if (expectedTurn !== undefined && expectedTurn !== target) {
          interaction.failed('payload_mismatch')
          return
        }
        const chat = identity.channel?.startsWith('ahp-chat:') ? identity.channel : (expectedBinding.chat_id ?? '')
        if (!sameSession(expectedBinding, identity, chat)) {
          interaction.failed('session_mismatch')
          return
        }
        expectedTurn = target
        expectedBinding = {
          ...expectedBinding,
          session_id: identity.sessionId,
          environment_id: identity.environmentId,
          ...(identity.taskId ? {task_id: identity.taskId} : {}),
          ...(identity.channel?.startsWith('ahp-chat:') ? {chat_id: identity.channel} : {}),
        }
        sessionVerified = true
        interaction.dependencyChanged('session', false)
      },
      sent(identity, channel, action) {
        sentObserved = true
        state.transportSent = true
        interaction.handoff(
          {
            session_id: identity.sessionId,
            environment_id: identity.environmentId,
            ...(identity.taskId ? {task_id: identity.taskId} : {}),
            chat_id: channel,
            ...(action.type === 'chat/turnCancelled' ? {turn_id: action.turnId} : {}),
          },
          expectedTurn === undefined
            ? undefined
            : action.type === 'chat/turnCancelled' && action.turnId === expectedTurn,
          sessionVerified ? sameSession(expectedBinding, identity, channel) : undefined,
        )
        state.release()
      },
      failed: reason => interaction.failed(reason ?? 'transport_failed'),
      unavailable: () => interaction.unresolved('handoff_unobserved'),
      waiting: (waiting, dependency = 'mutation_lane') => interaction.dependencyChanged(dependency, waiting),
      disposition: reason => interaction.unresolved(reason),
    }
  })
}

function surfaceForLocation(): ClientInteractionFact['surface'] {
  const path = location.pathname
  if (/^\/copilot\/tasks\//.test(path)) return 'global_task'
  if (/^\/copilot\/?$/.test(path)) return 'agents_page'
  if (/^\/[^/]+\/[^/]+\/tasks\//.test(path)) return 'repository_task'
  if (/^\/[^/]+\/[^/]+\/agents\/?$/.test(path)) return 'repository_agents'
  if (/^\/[^/]+\/[^/]+\/issues\//.test(path)) return 'issue_panel'
  if (/\/projects\//.test(path)) return 'memex_panel'
  if (path === '/' || path === '/dashboard') return 'dashboard'
  return 'unknown'
}

function visibility(): ClientInteractionFact['visibility'] {
  return document.visibilityState === 'visible'
    ? 'visible'
    : document.visibilityState === 'hidden'
      ? 'hidden'
      : 'unknown'
}

function visible(element: HTMLElement): boolean {
  if (
    visibility() !== 'visible' ||
    !element.isConnected ||
    element.closest('[hidden]') ||
    element.closest('[aria-hidden="true"]') ||
    !element.checkVisibility({checkOpacity: true, checkVisibilityCSS: true})
  ) {
    return false
  }
  const bounds = element.getBoundingClientRect()
  return (
    bounds.width > 0 &&
    bounds.height > 0 &&
    bounds.bottom > 0 &&
    bounds.right > 0 &&
    bounds.top < window.innerHeight &&
    bounds.left < window.innerWidth
  )
}

interface Feedback {
  readonly interaction: ClientInteraction
  check(): boolean
  readonly method: 'committed_control_visible_frame'
}

function createInteractionTracker() {
  return createBrowserClientInteractionTracker({
    now: browserObservationTime,
    nextSequence: nextBrowserObservationSequence,
    visibility,
    emit: fact => {
      interactionMetrics.observe(fact)
      sendEvent(
        CLIENT_INTERACTION_EVENT,
        {...fact},
        {
          privacy: 'content_free',
          onError: () => reportClientInteractionError('emission'),
        },
      )
    },
    reportError: (_error, reason) => reportClientInteractionError(reason),
  })
}

function beginInteraction(
  tracker: ReturnType<typeof createInteractionTracker>,
  context: CopilotClientInteractionContext,
  viewId: string,
  kind: ClientInteractionKind,
  event: Event | undefined,
  revision?: {id?: string; revision: number; pendingMessageId: string},
  handoffScope?: ClientInteractionStart['handoff_scope'],
) {
  const existing = event ? events.get(event) : undefined
  if (existing) return existing
  const id = crypto.randomUUID()
  const interaction = tracker.begin({
    ...context,
    surface: context.surface ?? surfaceForLocation(),
    client_view_id: viewId,
    client_interaction_id: id,
    interaction_kind: kind,
    eventTimeStamp:
      event instanceof Event &&
      event.target instanceof Node &&
      event.target.ownerDocument === document &&
      (!(event instanceof UIEvent) || !event.view || event.view === window)
        ? event.timeStamp
        : undefined,
    command_id: revision?.id ?? id,
    command_revision: revision?.revision ?? 1,
    ...(revision
      ? {pending_message_id: revision.pendingMessageId, handoff_scope: 'send_now_and_remove_queued' as const}
      : handoffScope
        ? {handoff_scope: handoffScope}
        : {}),
  })
  if (interaction && event) events.set(event, interaction)
  return interaction
}

export function createQueuedClientInteractionObserver(context: CopilotClientInteractionContext) {
  const active = new Map<ClientInteraction, () => void>()
  let viewId: string | undefined
  let tracker: ReturnType<typeof createInteractionTracker> | undefined
  const clearFeedback = () => {
    for (const cleanup of active.values()) cleanup()
  }
  const pageExit = () => {
    clearFeedback()
    tracker?.dispose('page_exit')
    interactionMetrics.pageExit()
  }
  const visibilityChanged = () => {
    if (visibility() === 'visible') return
    for (const interaction of active.keys()) interaction.unresolved('hidden')
    clearFeedback()
  }
  return {
    begin(message: Parameters<typeof createEstablishedAhpPendingMessage>[0], event: Event, button: HTMLButtonElement) {
      if (!isClientInteractionEnabled() || context.role !== 'writable' || !button.isConnected) return
      return observeClientInteraction(() => {
        if (events.has(event)) return events.get(event)
        if (!tracker) {
          tracker = createInteractionTracker()
          window.addEventListener('pagehide', pageExit)
          document.addEventListener('visibilitychange', visibilityChanged)
        }
        viewId ??= context.client_view_id ?? crypto.randomUUID()
        const key = pendingKey(context.session_id, context.chat_id, message.id)
        const previous = queuedRevisions.get(key)
        const interaction = beginInteraction(tracker, context, viewId, 'steer', event, {
          id: previous?.id,
          revision: (previous?.revision ?? 0) + 1,
          pendingMessageId: message.id,
        })
        if (!interaction) return
        if (interaction.commandId && interaction.commandRevision)
          retainRevision(key, interaction.commandId, interaction.commandRevision)
        queuedInteractions.set(message, interaction)
        interaction.preparationStarted()
        commands.set(interaction, {
          context,
          inputValue: message.message.text,
          expectedPendingMessageId: message.id,
          accepted: false,
          pending: true,
          transportSent: false,
          handoffRequired: true,
          requestFeedback: () => {},
          discardFeedback: () => active.get(interaction)?.(),
          release: () => active.get(interaction)?.(),
        })
        const prompt = recordClientCommandPreparation(createEstablishedAhpPendingMessage(message).prompt, true)
        prepareClientInteraction(interaction, prompt)
        const observer = clientCommandObserver(prompt)
        if (observer) bindClientCommand(message.message, observer)

        let frame: number | undefined
        let mutation: MutationObserver | undefined
        const cleanup = () => {
          active.delete(interaction)
          if (frame !== undefined) {
            const pendingFrame = frame
            frame = undefined
            observeClientInteraction(() => cancelAnimationFrame(pendingFrame))
          }
          observeClientInteraction(() => mutation?.disconnect())
          frame = undefined
        }
        const sample = () => {
          if (!active.has(interaction) || frame !== undefined) return
          frame = requestAnimationFrame(() => {
            frame = undefined
            if (visibility() !== 'visible') {
              interaction.unresolved('hidden')
              cleanup()
              return
            }
            const pending = observeClientInteraction(
              () =>
                visible(button) &&
                (button.disabled ||
                  button.getAttribute('aria-disabled') === 'true' ||
                  button.getAttribute('aria-busy') === 'true'),
            )
            if (pending === undefined) {
              interaction.unresolved('observer_failed')
              cleanup()
            } else if (pending) {
              interaction.feedback('committed_control_visible_frame')
              cleanup()
            }
          })
        }
        active.set(interaction, cleanup)
        if (active.size > MAX_PENDING_CLIENT_INTERACTIONS) {
          const oldest = active.entries().next().value
          if (oldest) {
            oldest[0].unresolved('observation_capacity')
            oldest[1]()
            reportClientInteractionError('observation_capacity')
          }
        }
        try {
          if (typeof button.checkVisibility !== 'function' || typeof requestAnimationFrame !== 'function') {
            interaction.unresolved('visibility_unsupported')
            reportClientInteractionError('observer')
            cleanup()
          } else {
            mutation = new MutationObserver(() => observeClientInteraction(sample))
            mutation.observe(button, {attributes: true})
            sample()
          }
        } catch {
          interaction.unresolved('observer_failed')
          reportClientInteractionError('observer')
          cleanup()
        }
        return interaction
      })
    },
    dispose() {
      clearFeedback()
      if (tracker) {
        window.removeEventListener('pagehide', pageExit)
        document.removeEventListener('visibilitychange', visibilityChanged)
      }
      tracker?.dispose()
      tracker = undefined
      viewId = undefined
    },
  }
}

export function createCopilotClientInteractionObserver(context: CopilotClientInteractionContext) {
  let beginCommand:
    | ((
        kind: 'send' | 'queue' | 'steer' | 'stop',
        event?: Event,
        handoffScope?: ClientInteractionStart['handoff_scope'],
      ) => ClientInteraction | undefined)
    | undefined
  return {
    reportError: () => reportClientInteractionError('observer'),
    command(
      kind: 'send' | 'queue' | 'steer' | 'stop',
      event?: Event,
      handoffScope?: ClientInteractionStart['handoff_scope'],
    ) {
      if (event) return observeClientInteraction(() => beginCommand?.(kind, event, handoffScope))
    },
    // Only handlers that already own an eligible commitment may lack a native input clock.
    committedCommand(
      kind: 'send' | 'queue' | 'steer' | 'stop',
      event?: Event,
      handoffScope?: ClientInteractionStart['handoff_scope'],
    ) {
      return observeClientInteraction(() => beginCommand?.(kind, event, handoffScope))
    },
    observe(
      input: HTMLTextAreaElement,
      container: HTMLElement,
      options?: {
        commandFeedback?: (
          kind: 'send' | 'queue' | 'steer' | 'stop',
          state: {accepted: boolean; pending: boolean},
          isVisible: (element: HTMLElement) => boolean,
        ) => boolean | undefined
      },
    ): () => void {
      if (!isClientInteractionEnabled()) return () => {}
      const clientViewId = context.client_view_id ?? crypto.randomUUID()
      const tracker = createInteractionTracker()
      const feedback = new Set<Feedback>()
      let frame: number | undefined
      let disposed = false
      let observationError: ClientInteractionUnknown | undefined
      let mutationObserver: MutationObserver | undefined
      const releaseFeedback = (item: Feedback) => {
        feedback.delete(item)
      }
      const activeCommands = new Set<ClientInteraction>()
      const schedule = () => {
        if (disposed || frame !== undefined || feedback.size === 0) return
        if (observationError) {
          for (const item of feedback) item.interaction.unresolved(observationError)
          feedback.clear()
          return
        }
        frame = requestAnimationFrame(() => {
          frame = undefined
          for (const item of feedback) {
            if (visibility() !== 'visible') {
              item.interaction.unresolved('hidden')
              releaseFeedback(item)
              continue
            }
            const matched = observeClientInteraction(item.check)
            if (matched === undefined) {
              item.interaction.unresolved('observer_failed')
              releaseFeedback(item)
            } else if (matched) {
              item.interaction.feedback(item.method)
              const state = commands.get(item.interaction)
              if (state && !state.handoffRequired) state.release()
              releaseFeedback(item)
            }
          }
        })
      }
      try {
        if (typeof input.checkVisibility !== 'function' || typeof requestAnimationFrame !== 'function') {
          observationError = 'visibility_unsupported'
          reportClientInteractionError('observer')
        } else {
          mutationObserver = new MutationObserver(() => observeClientInteraction(schedule))
          mutationObserver.observe(container, {attributes: true, childList: true, subtree: true})
        }
      } catch {
        observationError = 'observer_failed'
        reportClientInteractionError('observer')
      }
      const addFeedback = (item: Feedback) => {
        feedback.add(item)
        if (feedback.size > MAX_PENDING_CLIENT_INTERACTIONS) {
          const oldest = feedback.values().next().value
          if (oldest) {
            oldest.interaction.unresolved('observation_capacity')
            releaseFeedback(oldest)
            reportClientInteractionError('observation_capacity')
          }
        }
      }
      beginCommand = (kind, event, handoffScope) => {
        const interaction = beginInteraction(tracker, context, clientViewId, kind, event, undefined, handoffScope)
        if (!interaction || commands.has(interaction)) return interaction
        const initiallyReadOnly = input.readOnly
        const initiallyBusy = container.getAttribute('aria-busy') === 'true'
        const initiallyActionDisabled =
          container.querySelector('[data-testid="chat-composer-submit"]')?.getAttribute('aria-disabled') === 'true'
        const state: CommandState = {
          context,
          inputValue: input.value,
          accepted: false,
          pending: false,
          transportSent: false,
          handoffRequired: handoffScope !== 'not_applicable',
          requestFeedback: schedule,
          discardFeedback: () => {
            for (const item of feedback) {
              if (item.interaction === interaction) releaseFeedback(item)
            }
          },
          release: () => activeCommands.delete(interaction),
        }
        commands.set(interaction, state)
        activeCommands.add(interaction)
        addFeedback({
          interaction,
          method: 'committed_control_visible_frame',
          check: () => {
            if (!visible(input)) return false
            if (options?.commandFeedback) {
              const observed = options.commandFeedback(
                kind,
                {accepted: state.accepted, pending: state.pending},
                element => visible(element),
              )
              if (observed !== undefined) return observed
            }
            if (kind === 'stop') {
              return state.accepted && !container.querySelector('[data-testid="chat-composer-stop"]')
            }
            return (
              (state.accepted && state.inputValue.length > 0 && input.value === '') ||
              (state.pending &&
                ((!initiallyReadOnly && input.readOnly) ||
                  (!initiallyBusy && container.getAttribute('aria-busy') === 'true') ||
                  (!initiallyActionDisabled &&
                    container.querySelector('[data-testid="chat-composer-submit"]')?.getAttribute('aria-disabled') ===
                      'true')))
            )
          },
        })
        schedule()
        return interaction
      }
      const clearFeedback = () => {
        for (const item of feedback) releaseFeedback(item)
        feedback.clear()
        if (frame !== undefined) {
          const pendingFrame = frame
          frame = undefined
          observeClientInteraction(() => cancelAnimationFrame(pendingFrame))
        }
      }
      const pageExit = () => {
        clearFeedback()
        tracker.dispose('page_exit')
        interactionMetrics.pageExit()
      }
      const onVisibility = () => {
        if (visibility() !== 'visible') {
          for (const interaction of activeCommands) interaction.unresolved('hidden')
          activeCommands.clear()
          for (const item of feedback) item.interaction.unresolved('hidden')
          clearFeedback()
        } else {
          observeClientInteraction(schedule)
        }
      }
      document.addEventListener('visibilitychange', onVisibility)
      window.addEventListener('pagehide', pageExit)
      return () => {
        disposed = true
        beginCommand = undefined
        clearFeedback()
        observeClientInteraction(() => mutationObserver?.disconnect())
        document.removeEventListener('visibilitychange', onVisibility)
        window.removeEventListener('pagehide', pageExit)
        tracker.dispose()
        feedback.clear()
      }
    },
  }
}
